/*
 * @Author: liuhongzhuang
 * @Date: 2023-05-20
 * @Description: 基于MUI的轻量级Breadcrumbs组件
 */
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Typography,
} from '@mui/material';
import Link from 'next/link';
import React, { useContext } from 'react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbsProps extends Omit<MuiBreadcrumbsProps, 'children'> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  className?: string;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const Breadcrumbs = ({
  items,
  separator = '/',
  className = '',
  SDProductDetailsPageBtnClick,
  ...props
}: BreadcrumbsProps) => {
  // const theme = useMuiTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 移动端不显示面包屑导航
  // if (isMobile) return null;
  const { deviceType } = useContext(ResponsiveContext);
  const waiMode = useStore((state) => state.waiMode);
  // 移动端样式
  let mobileStyleProps = {};
  // pc端样式
  let pcStyleProps = {};
  if (deviceType === EPlatform.WAP) {
    mobileStyleProps = {
      className: 'text-12 font-br',
      sx: {
        '& .MuiBreadcrumbs-separator': {
          mx: 1,
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          fontSize: '3.2vw',
        },
        '& a': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          display: 'block',
          height: '3.733vw',
        },
        '& p': {
          fontSize: '3.2vw',
          fontFamily: 'var(--font-br)',
        },
        '& .breadcrumbs-item': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          fontSize: '3.2vw',
          lineHeight: '3.733vw',
          display: 'block',
        },
        '& .MuiTypography-root': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          fontSize: '3.2vw',
          lineHeight: '3.733vw',
          display: 'block',
        },
      },
    };
  } else {
    pcStyleProps = {
      sx: {
        '& .MuiBreadcrumbs-separator': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
        '& a': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
        '& .breadcrumbs-item': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
        '& .MuiTypography-root': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
      },
    };
  }

  return (
    <MuiBreadcrumbs
      separator={separator}
      aria-label="breadcrumb"
      {...mobileStyleProps}
      {...pcStyleProps}
      {...props}
      className={className}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        if (isLast) {
          return (
            <Typography
              key={item.label}
              sx={{ color: waiMode ? colors.primaryBlack : colors.primaryText }}
            >
              {item.label}
            </Typography>
          );
        }

        if (item.onClick) {
          return (
            <span
              key={index}
              className="breadcrumbs-item f14 !no-underline font-br text-primary-black hover:text-primary-text cursor-pointer"
              onClick={() => {
                SDProductDetailsPageBtnClick?.('breadcrumb');
                item.onClick?.();
              }}
            >
              {item.label}
            </span>
          );
        }

        return (
          <Link
            key={index}
            className="!no-underline"
            href={item.href || '#'}
            onClick={() => {
              SDProductDetailsPageBtnClick?.('breadcrumb');
            }}
          >
            <span
              className={`breadcrumbs-item f14 no-underline font-br ${waiMode ? 'text-primary-black' : 'text-auxiliary-text'} hover:text-primary-text hover:underline`}
            >
              {item.label}
            </span>
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
